import {
  AfterViewInit,
  Component,
  forwardRef,
  ChangeDetectionStrategy,
  signal,
  WritableSignal,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  BehaviorSubject,
  firstValueFrom,
  Observable,
  ReplaySubject,
} from 'rxjs';
import { delay, filter, map } from 'rxjs/operators';
import { ScaleQuestionAnswer } from '../../../../models/answer/scale-answer.model';
import { IntRange } from '../../../../models/int-range/int-range';
import { RadioQuestion } from '../../../../models/questions/radio-question.model';
import { ScaleQuestionLabel } from '../../../../models/questions/scale-question-label.model';
import { OnLoaded } from '@lyceo/shared/dynamic-component-loading/feature-main';
import {
  QuestionAnswer,
  QuestionControlType,
} from '../question-type.interface';
import { QuestionDisplayComponent } from '../../question-display/question-display.component';
import { IonRadioGroup, IonItem, IonRadio } from '@ionic/angular/standalone';
import { AsyncPipe } from '@angular/common';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-radio-question',
  templateUrl: './radio-question.component.html',
  styleUrls: ['./radio-question.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioQuestionComponent),
      multi: true,
    },
  ],
  imports: [
    QuestionDisplayComponent,
    IonRadioGroup,
    IonItem,
    IonRadio,
    AsyncPipe,
  ],
})
export class RadioQuestionComponent
  implements AfterViewInit, OnLoaded, QuestionControlType
{
  public _question$: BehaviorSubject<RadioQuestion> =
    new BehaviorSubject<RadioQuestion>(undefined as never);

  public set question(question: RadioQuestion) {
    question.scaleQuestionLabels = question.scaleQuestionLabels.sort(
      (a, b) => a.onScalePoint - b.onScalePoint,
    );
    this._question$.next(question);
  }

  public get question$(): Observable<RadioQuestion> {
    return this._question$.pipe(filter((question) => !!question));
  }

  public set answer(answer: ScaleQuestionAnswer) {
    this.onSelect(answer);
  }

  public get answer(): ScaleQuestionAnswer {
    return this.currentAnswer$.getValue();
  }

  public currentAnswer$: BehaviorSubject<ScaleQuestionAnswer> =
    new BehaviorSubject<ScaleQuestionAnswer>(undefined as never);

  private pristine: boolean = true;

  /**
   * Subject which starts to emit when the view is rendered. This is needed
   * because the component is dynamically loading.
   */
  public onFinishedInit$: ReplaySubject<void> = new ReplaySubject<void>();

  protected readonly disabled: WritableSignal<boolean> = signal(false);

  /**
   * @inheritDoc
   */
  public ngAfterViewInit(): void {
    this.onFinishedInit$.next();
    this.onFinishedInit$.complete();
  }

  /**
   * @inheritDoc
   */
  public ready(): Promise<void> {
    return firstValueFrom(this.onFinishedInit$.pipe(delay(20)));
  }

  /**
   * @inheritDoc
   */
  public isPristine(): boolean {
    return this.pristine;
  }

  /**
   * @inheritDoc
   */
  public getValue(): QuestionAnswer {
    return this.currentAnswer$.getValue();
  }

  /**
   * @inheritDoc
   */
  public onValueChanges(): Observable<QuestionAnswer> {
    return this.currentAnswer$.asObservable();
  }

  /**
   * @inheritDoc
   */
  public isValid(): boolean {
    return this.isValidOption(this.currentAnswer$.getValue());
  }

  /**
   * @inheritDoc
   */
  public onValidChanges(): Observable<boolean> {
    return this.currentAnswer$.pipe(
      map((option) => this.isValidOption(option)),
    );
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  public getOptions(): number[] {
    const scaleRange: IntRange = this._question$.getValue().scaleRange;
    const steps: number = scaleRange.totalSteps();
    return [...Array(steps).keys()].map((i) => i + scaleRange.min);
  }

  public getOptionLabel(option: number): string | undefined {
    const labels: ScaleQuestionLabel[] =
      this._question$.getValue().scaleQuestionLabels;
    return labels.find((label) => label.onScalePoint === option)?.title;
  }

  public onSelect(option: number): void {
    if (!this.isValidOption(option)) {
      this.currentAnswer$.next(undefined as never);
      return;
    }
    this.currentAnswer$.next(option);
    this.pristine = false;
  }

  private isValidOption(option: number): boolean {
    return this.getOptions().indexOf(option) !== -1;
  }
}
